// @ts-strict-ignore
import { Connection } from '@3d-scene/model3d/connection';
import { ArrayUtils } from '@shared/util/array-utils';
import { TextureConnectionUtils } from '@3d-scene/processor/texture-connection-utils';
import { ProjectModel } from '@project/model/project.model';
import { SelectionModel } from '../../../article/model/selection.model';
import { PropertyCreator } from '@article/util/property-creator';
import { GroupedCubesSearchRequirements } from '@project/model/vo/grouped-cubes-search-requirements';
import { ArticleTypeIds } from '@article/constants/article-type-ids';
import { ArticleDataService } from '@article/service/article-data.service';
import { FavoriteArticlesWrapperVO, FavoriteArticleVO } from '@user/components/favorites/models/favorite-article.vo';
import { combineLatest, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { map, mergeMap, takeUntil } from 'rxjs/operators';
import { SuitableConnectionsVO } from '../../../article/model/vo/suitable-connections.vo';
import { ArticleCreationManager } from '../../../article/manager/article-creation.manager';
import { NumberUtils } from '@shared/util/number-utils';
import { ConnectionAreaUsageVO, MetaElementUsageIdentityVO, MetaElementVO, SelectionMetaElementUsageIdentity, TextureAreaUsageVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { LabelEditorMode } from '../label-editor.component';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleInfoVO } from '@project/model/vo/project.vo';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleContainerPropertyDef } from '@article/constants/article-container-property-def';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { Vector3 } from 'three';
import { SelectionArticleGroupIdentity } from '../../../article/model/vo/selection-article-group-identity';
import { ProjectHelper } from '@project/helper/project.helper';
import { ConnectionCreator } from '@article/components/connections/connection-creation/connection-creator';
import { IOperation } from '@shared/undo/operation/i-operation';
import { LoadConnectionTextureProcessor } from '@3d-scene/processor/load-connection-texture.processor';
import { TextureUtils } from '@3d-scene/model3d/util/texture-utils';
import { UndoAttachUnusedLabelingArticlesOperation } from '@shared/undo/operation/undo-attach-unused-labeling-articles-operation';
import { CloneUtils } from '@shared/util/clone-utils';
import { ArticleRefreshData } from '@article/service/model/article-refresh-data';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { ArticleLoadingManager } from '@article/service/article-loading.manager';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { MetaElementUtils } from '@article/components/connections/connection-creation/meta-element.utils';
import { Injectable } from '@angular/core';
import { ArticleCreationManagerFactory } from '../../../article/manager/article-creation.manager.factory';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { WireBridgeService } from '../../../article/manager/wire-bridge.service';
import { AttachedArticleContainer } from '../../../article/service/article-container-attached.service';
import { LabelEditorStateManager } from './label-editor-state.manager';
import { ArticleCreationService } from '../../../article/service/article-creation.service';
import { Log } from '@shared/debug/log';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '../../../article/service/selection-model.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { UpdateLabelingCompressionProcessor } from '@3d-scene/processor/update-labeling-compression.processor';
import { ArticleContainerService } from '@3d-scene/service/article-container.service';
import { AccessoryService, ConnectionSearchResult } from '@article/service/accessory.service';
import { ConnectionCubeSearchVector } from '@article/components/connections/connection-creation/search/connection-cube-search-vector';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { ArticlesMovedHandlerData } from '../../../article/manager/article-move.manager';

@Injectable({ providedIn: 'root' })
export class UnusedLabelingService {
  public static readonly UNUSED_LABELING_ARTICLES_ADDED_MESSAGE_TYPE: string = 'unusedLabelingArticlesAdded';

  private unusedLabelingConnections: Connection[];
  private articleCreationManager: ArticleCreationManager;
  private favoriteArticles: FavoriteArticleVO[];

  private createdArticleContainers: ArticleContainer[];
  private createdConnectionCubes: Connection[];

  constructor(
    private propertyCreator: PropertyCreator,
    private projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    private projectMetaElementService: ProjectMetaElementService,
    private selectionModel: SelectionModel,
    private selectionModelService: SelectionModelService,
    private articleDataService: ArticleDataService,
    private projectHelper: ProjectHelper,
    private loadConnectionTextureProcessor: LoadConnectionTextureProcessor,
    private articleContainerLoader: ArticleContainerLoader,
    private articleCreationManagerFactory: ArticleCreationManagerFactory,
    private articleContainerFactory: ArticleContainerFactory,
    private containerService: ContainerService,
    private connectionCreator: ConnectionCreator,
    private wireBridgeService: WireBridgeService,
    private textureConnectionUtils: TextureConnectionUtils,
    private articleContainerUtils: ArticleContainerUtils,
    private articleLoadingManager: ArticleLoadingManager,
    private labelEditorStateManager: LabelEditorStateManager,
    private _articleCreationService: ArticleCreationService,
    private applicationRefreshService: ApplicationRefreshService,
    private detachArticleUsageService: DetachArticleUsageService,
    private updateLabelingCompressionProcessor: UpdateLabelingCompressionProcessor,
    private articleContainerService: ArticleContainerService,
    private accessoryService: AccessoryService,
    private connectionInfoService: ConnectionInfoService,
  ) {
  }

  public labelEditorModeChangedHandler(mode: LabelEditorMode): void {
    if (mode === LabelEditorMode.CLOSED) {
      this.clean();
    }
  }

  public favoriteArticlesUpdatedHandler(): void {
    this.favoriteArticles = null;
  }

  public articleContainerMovedHandler(data: ArticlesMovedHandlerData): void {
    if (
      ArrayUtils.isEmpty(this.createdArticleContainers) ||
      ArrayUtils.isEmpty(data.articleUsageIds) ||
      (data.articleDelta && MathUtils.equalsNumber3D(data.articleDelta, new Vector3(0, 0, 0)))
    ) {
      return;
    }
    this.createdArticleContainers.forEach((articleContainer) => {
      const backwardArticleContainer: ArticleContainer = this.articleContainerUtils.getBackwardArticleContainerOnRailRecursively(articleContainer);
      if (backwardArticleContainer && data.articleUsageIds.includes(backwardArticleContainer.articleGUID)) {
        let offset: Vector3;
        if (data.articleDelta) {
          offset = data.articleDelta;
        } else if (data.moveArticlesMap && data.moveArticlesMap.containsKey(backwardArticleContainer.articleGUID)) {
          offset = data.moveArticlesMap.getValue(backwardArticleContainer.articleGUID);
        }
        if (offset) {
          articleContainer.position.add(offset);
        }
      }
    });
  }

  public createUnusedLabelingConnectionCubes(cancelSubject: Subject<void>): Observable<Connection[]> {
    this.clean();
    this.unusedLabelingConnections = this.textureConnectionUtils.getSelectedUnusedLabelingConnectionsWithRelative();
    if (ArrayUtils.isEmpty(this.unusedLabelingConnections)) {
      return of([]);
    }

    const suitableConnections = this.getSuitableConnections();
    const favoriteArticles = this.favoriteArticles ? of(this.favoriteArticles) : this.getFavoriteArticles();
    return combineLatest([suitableConnections, favoriteArticles]).pipe(
      takeUntil(cancelSubject),
      mergeMap(
        async tuple => {
          const connectionSearchResult = tuple[0];
          this.favoriteArticles = tuple[1];
          const suitableConnections = connectionSearchResult.vo.connections;
          if (ArrayUtils.isEmpty(this.favoriteArticles) || ArrayUtils.isEmpty(suitableConnections)) {
            return [];
          } else {
            this.setCreationManager();
            return await this.createArticleContainers(this.favoriteArticles.concat(), suitableConnections, connectionSearchResult.connectionCubeVectors);
          }
        }
    ));
  }

  private getFavoriteArticles(): Observable<FavoriteArticleVO[]> {
    return this.articleDataService.getFavoriteArticles([ArticleTypeIds.LABELING], false).pipe(
      map((wrappers: FavoriteArticlesWrapperVO[]) => {
        const labelingWrapper: FavoriteArticlesWrapperVO = wrappers.find((wrapper) => wrapper.articleTypeId === ArticleTypeIds.LABELING);
        if (!labelingWrapper || !labelingWrapper.favoriteArticles) {
          return null;
        } else {
          return labelingWrapper.favoriteArticles.sort((a: FavoriteArticleVO, b: FavoriteArticleVO) => {
            return NumberUtils.numericCompare(a.sortOrder, b.sortOrder);
          });
        }
      })
    );
  }

  private getSuitableConnections(): Observable<ConnectionSearchResult> {
    const groupedCubesSearchRequirements: GroupedCubesSearchRequirements[] = [
      new GroupedCubesSearchRequirements(this.propertyCreator.createCubesSearchRequirementsByDefault(this.unusedLabelingConnections)),
    ];
    return this.accessoryService.getArticlesByGroupedRequirements(groupedCubesSearchRequirements);
  }

  private setCreationManager(): void {
    this.articleCreationManager = this.articleCreationManagerFactory.getNewInstance();
    this.articleCreationManager.isUnusedLabeling = true;
    this.articleCreationManager.showFaultMessage = false;
    this.articleCreationManager.createUndoOperation = false;
    this.articleCreationManager.removeSelection = false;
    this.articleCreationManager.messageType = UnusedLabelingService.UNUSED_LABELING_ARTICLES_ADDED_MESSAGE_TYPE;
  }

  private cleanCreationManager(): void {
    if (this.articleCreationManager) {
      this.articleCreationManager.clean();
      this.articleCreationManager = null;
    }
  }

  private async createArticleContainers(favoriteArticles: FavoriteArticleVO[],
                                        suitableConnections: SuitableConnectionsVO[],
                                        connectionCubeVectors: ConnectionCubeSearchVector[],
                                        createdArticleContainersResult: ArticleContainer[] = []): Promise<Connection[]> {
    if (this.isCreationCompleted(favoriteArticles, connectionCubeVectors)) {
      return this.onCreationCompleted(createdArticleContainersResult);
    }
    const currentFavoriteArticle = favoriteArticles.shift();
    const suitableConnection = suitableConnections.find((connection) => connection.article.identity === currentFavoriteArticle.articleId);

    if (suitableConnection) {
      const resultHandler = firstValueFrom(this._articleCreationService.articlesAdded$.pipe(
        map((data) => {
          if (data.type !== this.articleCreationManager.messageType) {
            return;
          }
          this.articleAddedHandler(
            createdArticleContainersResult,
            connectionCubeVectors
          );
        })));
      await this.articleCreationManager.createArticlesBySuitableConnections(suitableConnection, connectionCubeVectors);
      try {
        await resultHandler;
        return await this.createArticleContainers(favoriteArticles, suitableConnections, connectionCubeVectors, createdArticleContainersResult);
      } catch (error) {
        Log.error('Error in added articles subscription: ', error);
      }
    }
    return await this.createArticleContainers(favoriteArticles, suitableConnections, connectionCubeVectors, createdArticleContainersResult);
  }

  private articleAddedHandler(
    createdArticleContainersResult: ArticleContainer[],
    connectionCubeVectors: ConnectionCubeSearchVector[]
  ): void {
    const createdArticleContainers: ArticleContainer[] = this.articleCreationManager.getCreatedArticleContainers();
    createdArticleContainersResult.push(...createdArticleContainers);
    this.filterConnectionCubeVectors(createdArticleContainers, connectionCubeVectors);
    this.articleCreationManager.clean();
    this.articleCreationManager.removeSelection = false;
    this.articleCreationManager.messageType = UnusedLabelingService.UNUSED_LABELING_ARTICLES_ADDED_MESSAGE_TYPE;
  }

  private isCreationCompleted(favorites: FavoriteArticleVO[],
                              connectionCubeVectors: ConnectionCubeSearchVector[],): boolean {
    if (ArrayUtils.isEmpty(favorites)) {
      return true;
    }
    return connectionCubeVectors.every((vector) => ArrayUtils.isEmpty(vector.sortedCubesSearchRequirements));
  }

  private filterConnectionCubeVectors(createdArticleContainers: ArticleContainer[],
                                      connectionCubeVectors: ConnectionCubeSearchVector[],): void {
    const backwardConnectionIdentities: MetaElementUsageIdentityVO[] = [];
    for (const articleContainer of createdArticleContainers) {
      backwardConnectionIdentities.push(...ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(articleContainer.articleUsage));
    }
    for (const vector of connectionCubeVectors) {
      vector.sortedCubesSearchRequirements = vector.sortedCubesSearchRequirements.filter((searchRequirements) => {
        return !backwardConnectionIdentities.find((identity) => identity.equals(searchRequirements.identity));
      });
    }
  }

  private onCreationCompleted(createdArticleContainers: ArticleContainer[]): Connection[] {
    this.cleanCreationManager();
    this.createdArticleContainers = createdArticleContainers;
    this.createdArticleContainers.forEach((articleContainer: ArticleContainer) => articleContainer.removeEventListeners());
    this.createdConnectionCubes = this.textureConnectionUtils.getTextureConnectionsByArticles(this.createdArticleContainers, Connection.TEXT_TYPES);
    return this.createdConnectionCubes;
  }

  private clean(): void {
    this.unusedLabelingConnections = null;
    this.createdConnectionCubes = null;
    if (this.createdArticleContainers) {
      this.createdArticleContainers.forEach((articleContainer) => {
        articleContainer.disposeModel();
      });
    }
    this.createdArticleContainers = null;
  }

  public async attachCreatedArticleContainers(isDirectPrintingMode: boolean): Promise<IOperation[]> {
    const undoAttachOperations: IOperation[] = [];
    if (ArrayUtils.isEmpty(this.createdArticleContainers) || isDirectPrintingMode) {
      return undoAttachOperations;
    }
    const articleContainersToAttach: ArticleContainer[] = await this.getArticleContainersToAttach();
    if (ArrayUtils.isNotEmpty(articleContainersToAttach)) {
      this.labelEditorStateManager.setLabelEditorUpdateState(false);
      const oldSelection: any[] = this.selectionModel.selectedObjects.concat();
      for (const articleContainer of articleContainersToAttach) {
        this.projectArticleService.storeArticleUsage(articleContainer.articleUsage);
        this.createUsagesForAcceptingConnections(articleContainer);
        if (ArticleUtils.isDynamicArticle(articleContainer.article)) {
          this.addDynamicArticleInfo(articleContainer);
        }
        await this.addArticleToCompositionContainer(articleContainer);
        if (this.selectionModel.isConnectionSelection()) {
          this.updateSelection(articleContainer);
        }
      }
      this.labelEditorStateManager.setLabelEditorUpdateState(true);

      const shouldUpdateDirectPrintingTableProvider: boolean = this.shouldUpdateDirectPrintingTableProvider(articleContainersToAttach);
      if (shouldUpdateDirectPrintingTableProvider) {
        this.labelEditorStateManager.notifyUpdateDirectPrintingTableProvider();
      }
      const undoAttachOperation: UndoAttachUnusedLabelingArticlesOperation = new UndoAttachUnusedLabelingArticlesOperation(
        articleContainersToAttach,
        this.projectModel,
        this.projectArticleService,
        this.projectMetaElementService,
        this.selectionModel,
        this.selectionModelService,
        oldSelection,
        shouldUpdateDirectPrintingTableProvider,
        this.wireBridgeService,
        this.articleContainerLoader,
        this.articleContainerFactory,
        this.containerService,
        this.applicationRefreshService,
        this.detachArticleUsageService,
        this.labelEditorStateManager
      );
      if (undoAttachOperation) {
        undoAttachOperations.push(undoAttachOperation);
      }
      const undoCleanTextureOperation: IOperation =
        this.textureConnectionUtils.cleanMultipurposeTextureConnections(articleContainersToAttach, this.loadConnectionTextureProcessor);
      if (undoCleanTextureOperation) {
        undoAttachOperations.push(undoCleanTextureOperation);
      }
    }
    this.projectModel.notifyCurrentProjectChanged();
    await this.updateLabelingCompressionProcessor.updateTextures();
    return undoAttachOperations;
  }

  private async getArticleContainersToAttach(): Promise<ArticleContainer[]> {
    const articleContainersToAttachIds: string[] = [];
    for (const connectionCube of this.createdConnectionCubes) {
      const articleContainerId: string = connectionCube.articleContainer.articleUsage.id;
      if (
        !articleContainersToAttachIds.includes(articleContainerId) &&
        !this.containerService.findArticleContainerInGlobal(articleContainerId) &&
        TextureUtils.hasConnectionTextureText(connectionCube)
      ) {
        articleContainersToAttachIds.push(articleContainerId);
      }
    }
    if (ArrayUtils.isEmpty(articleContainersToAttachIds)) {
      return [];
    }
    const articleContainersToAttach: ArticleContainer[] =
      this.createdArticleContainers.filter((container: ArticleContainer) => articleContainersToAttachIds.includes(container.articleUsage.id));

    return this.cloneArticleContainers(articleContainersToAttach);
  }

  private createUsagesForAcceptingConnections(articleContainer: ArticleContainer): void {
    for (const givingConnectionUsage of ArticleUsageUtils.backwardConnectionAreaUsages(articleContainer.articleUsage)) {
      const acceptingCube: Connection = this.connectionInfoService.findConnectionByIdentity(givingConnectionUsage.connection.acceptingAreaId);
      const acceptingArticleContainer: ArticleContainer = this.containerService.getArticleContainer(acceptingCube);
      if (!acceptingCube.metaElementUsage) {
        acceptingCube.metaElementUsage = this.projectMetaElementService.addMetaElementUsage(acceptingArticleContainer.articleGUID, acceptingCube.metaElementVO);
      }
      const acceptingArea: ConnectionAreaUsageVO = acceptingCube.metaElementUsage as ConnectionAreaUsageVO;
      acceptingArea.connection = givingConnectionUsage.connection;
    }
  }

  private addDynamicArticleInfo(articleContainer: ArticleContainer): void {
    const originalDynamicMetaElements: MetaElementVO[] = articleContainer.properties.getValue(ArticleContainerPropertyDef.ORIGINAL_META_ELEMENTS);
    const articleInfo: ArticleInfoVO = this.articleContainerService.createDynamicArticleInfo(
      articleContainer,
      originalDynamicMetaElements
    );
    this.projectModel.currentProject.dynamicArticlesInfo.push(articleInfo);
  }

  private async addArticleToCompositionContainer(articleContainer: ArticleContainer): Promise<void> {
    const backwardConnectionUsageIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(
      articleContainer.articleUsage
    );
    const acceptingCube: Connection = this.connectionInfoService.findConnectionByIdentity(backwardConnectionUsageIds[0]);
    const acceptingArticleContainer: ArticleContainer = this.containerService.getArticleContainer(acceptingCube);
    const compositionContainer = this.containerService.getCompositionContainer(acceptingArticleContainer);

    const data: AttachedArticleContainer = {
      articleGUID: articleContainer.articleGUID,
      compositionId: compositionContainer.compositionGuid,
      container: articleContainer,
      connectedTo: backwardConnectionUsageIds,
    };
    await this.containerService.attachArticleContainer(data);
  }

  private updateSelection(articleContainer: ArticleContainer): void {
    articleContainer.refreshTextureConnections(false);
    const backwardConnectionUsageIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(
      articleContainer.articleUsage
    );
    for (const backwardConnectionUsageId of backwardConnectionUsageIds) {
      const acceptingCube: Connection = this.connectionInfoService.findConnectionByIdentity(backwardConnectionUsageId);
      const linkedConnections: Connection[] = this.connectionCreator.getMultipurposeConnectionsBySelection(acceptingCube);
      linkedConnections.forEach((connection) => {
        if (connection.selected) {
          const acceptingArticleContainer: ArticleContainer = this.containerService.getArticleContainer(connection);
          const connectionSelectionIdentity: SelectionMetaElementUsageIdentity = new SelectionMetaElementUsageIdentity(
            acceptingArticleContainer.articleGUID,
            connection
          );
          this.selectionModelService.unSelectObject(connectionSelectionIdentity);
        }
      });
    }
    if (!this.selectionModel.isConnectionSelection()) {
      const articleSelectionIdentity: SelectionArticleGroupIdentity = this.projectHelper.createSelection(articleContainer.articleUsage);
      this.selectionModelService.selectObject(articleSelectionIdentity, false, false);
    }
    for (const connection of articleContainer.connections) {
      if (connection.metaElementVO instanceof TextureAreaVO) {
        const textureUsage: TextureAreaUsageVO = connection.metaElementUsage as TextureAreaUsageVO;
        if (!textureUsage.previousMergedUsageId) {
          const connectionSelectionIdentity: SelectionMetaElementUsageIdentity = new SelectionMetaElementUsageIdentity(
            articleContainer.articleGUID,
            connection
          );
          this.selectionModelService.selectObject(connectionSelectionIdentity, this.selectionModel.isConnectionSelection(), false, true);
        }
      }
    }
  }

  private async cloneArticleContainers(articleContainers: ArticleContainer[]): Promise<ArticleContainer[]> {
    const clonedArticleContainers: ArticleContainer[] = [];
    for (const articleContainer of articleContainers) {
      const cloned: ArticleContainer = this.articleContainerFactory.getNewInstance();
      cloned.articleGUID = articleContainer.articleGUID;
      cloned.properties = articleContainer.properties;
      cloned.updateArticleUsage(this.cloneArticleUsage(articleContainer.articleUsage));
      cloned.rotateByEuler(articleContainer.rotation);
      cloned.moveTo(articleContainer.position.x, articleContainer.position.y, articleContainer.position.z);
      clonedArticleContainers.push(cloned);
    }
    const refreshData: ArticleRefreshData = new ArticleRefreshData();
    refreshData.deleteUnusedConnections = false;

    try {
      await this.articleContainerLoader.refreshContainers(clonedArticleContainers, refreshData);
    } catch (error) {
      await this.detachArticleUsageService.detachArticleUsageFromProjectByIds(ArticleContainerUtils.extractArticleGUIDs(clonedArticleContainers));
      throw error;
    }
    clonedArticleContainers.forEach((articleContainer: ArticleContainer) => {
      if (ArticleUtils.isDynamicArticle) {
        this.articleLoadingManager.dispatchArticleChangeMessage(articleContainer, false);
      }
    });
    return clonedArticleContainers;
  }

  private cloneArticleUsage(articleUsage: ArticleUsageVO): ArticleUsageVO {
    const clonedArticleUsage: ArticleUsageVO = CloneUtils.cloneArticleUsageVO(articleUsage);
    clonedArticleUsage.metaElementUsages =
      ArticleUsageUtils.nonTextureAreaUsages(clonedArticleUsage).concat(ArticleUsageUtils.textureAreaUsages(articleUsage));
    return clonedArticleUsage;
  }

  private shouldUpdateDirectPrintingTableProvider(articleContainers: ArticleContainer[]): boolean {
    let shouldUpdate: boolean = false;
    for (const articleContainer of articleContainers) {
      for (const givingConnectionUsage of ArticleUsageUtils.backwardConnectionAreaUsages(articleContainer.articleUsage)) {
        const acceptingCube: Connection = this.connectionInfoService.findConnectionByIdentity(givingConnectionUsage.connection.acceptingAreaId);
        if (acceptingCube && MetaElementUtils.isMultipurposeArea(acceptingCube.metaElementVO)) {
          shouldUpdate = true;
          break;
        }
      }
      if (shouldUpdate) {
        break;
      }
    }
    return shouldUpdate;
  }
}
